'use client';

import ListingActionButtons from '@c/listing/ListingActionButtons';

import { useEffect, useRef, useState } from 'react';
import { useCreateListing } from '../_context';
import { CarListingPackage } from './CarListingPackage';
import CarsLocation from './CarsLocation';
import EditCategoryModal from './EditCategoryModal';
import FeaturesSection from './FeaturesSection';
import { MediaSelect } from './MediaSelect';
import { ProductInfo } from './ProductInfo';
import { ProductPriceAndInventory } from './ProductPriceAndInventory';
import { Shipping } from './Shipping';
import { CategorySummary } from './CategorySummary';

export const CreateListingStepFive = () => {
  const {
    form,
    onSubmit,
    onSubmitError,
    product,
    unknownError,
    checkIncludes,
    isVehicleListing,
  } = useCreateListing();
  const [editCategoryModalOpen, setEditCategoryModalOpen] = useState(false);

  const mediaSelectRef = useRef<HTMLDivElement>(null);
  const productInfoRef = useRef<HTMLDivElement>(null);
  const gearFitmentRef = useRef<HTMLDivElement>(null);
  const bikeFitmentRef = useRef<HTMLDivElement>(null);
  const featuresSectionRef = useRef<HTMLDivElement>(null);
  const shippingRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const errors = form.formState.errors;
    const hasMediaErrors = errors.images || errors.video;
    const hasProductInfoErrors =
      errors.title || errors.description || errors.brand || errors.condition;
    const hasGearFitmentErrors = errors.size || errors.variations;
    const hasFeaturesSectionErrors =
      errors.floor_price || errors.minimum_offer_price;
    const hasShippingErrors = 'shipping' in errors;
    const hasPriceErrors = errors.price;
    const hasBikeErrors = 'fitting_bikes' in errors;
    const hasSourcesErrors = 'sources' in errors;

    if (hasMediaErrors) {
      mediaSelectRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (hasProductInfoErrors) {
      productInfoRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (hasGearFitmentErrors) {
      gearFitmentRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (hasFeaturesSectionErrors) {
      featuresSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (hasShippingErrors) {
      shippingRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    if (hasPriceErrors || hasSourcesErrors) {
      priceRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    if (hasBikeErrors) {
      bikeFitmentRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [form.formState.errors]);

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit, onSubmitError)}
      className="flex w-full flex-col gap-12 py-16 sm:mx-auto sm:px-8 lg:max-w-content lg:py-12 xl:px-60"
    >
      <div className="flex items-center gap-x-4">
        {product.is_draft && (
          <div className="flex items-center justify-center rounded-2xl bg-brand-secondary px-4 py-2 text-[1.8rem] font-medium text-white">
            Draft
          </div>
        )}
        {(product.integration_info?.type === 'ebay' ||
          product.integration_info?.type === 'shopify') && (
          <div className="flex items-center justify-center rounded-2xl bg-brand-secondary px-4 py-2 text-[1.8rem] font-medium text-white">
            This item is externally synced and cannot be edited from Motorsport
            Locker
          </div>
        )}
      </div>
      <CategorySummary setEditCategoryModalOpen={setEditCategoryModalOpen} />
      <div ref={mediaSelectRef}>
        <MediaSelect />
      </div>
      <div ref={productInfoRef}>
        <ProductInfo />
      </div>
      <div ref={priceRef}>
        <ProductPriceAndInventory />
      </div>
      {isVehicleListing ? <CarsLocation /> : null}
      {!isVehicleListing ? (
        <div ref={featuresSectionRef}>
          <FeaturesSection />
        </div>
      ) : null}
      {!checkIncludes(['giveaways']) && !isVehicleListing && (
        <div ref={shippingRef}>
          <Shipping />
        </div>
      )}
      {isVehicleListing ? (
        <div ref={shippingRef}>
          <CarListingPackage />
        </div>
      ) : null}
      {unknownError && (
        <div className="text-[1.8rem] text-red-500">{unknownError}</div>
      )}
      {form.formState.errors &&
        Object.keys(form.formState.errors).length > 0 && (
          <div className="text-[1.8rem] text-red-500">
            {Object.entries(form.formState.errors).map(([key, error]) => (
              <div key={key}>
                {key}: {error.message?.toString()}
              </div>
            ))}
          </div>
        )}
      <ListingActionButtons />
      <EditCategoryModal
        isOpen={editCategoryModalOpen}
        dismiss={() => setEditCategoryModalOpen(false)}
      />
    </form>
  );
};
